import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Pane,
  Heading,
  Table,
  Text,
  Link,
  Spinner,
  Alert,
  Button,
  TextInputField,
  SelectField,
  IconButton,
  ChevronLeftIcon,
  ChevronRightIcon,
  Badge
} from 'evergreen-ui';
import moment from 'moment';
import Api from '../../../util/Api';

class AllReviews extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reviews: [],
      loading: true,
      error: null,
      pagination: {
        page: 1,
        limit: 20,
        totalCount: 0,
        totalPages: 0
      },
      stats: null,
      filters: {
        search: '',
        rating: '',
        sort: 'createdAt',
        direction: 'DESC'
      }
    };
  }

  componentDidMount() {
    this.fetchReviews();
    this.fetchStats();
  }

  fetchReviews = async () => {
    try {
      this.setState({ loading: true, error: null });

      const { pagination, filters } = this.state;
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sort: filters.sort,
        direction: filters.direction
      });

      if (filters.search) {
        params.append('search', filters.search);
      }

      if (filters.rating) {
        params.append('rating', filters.rating);
      }

      const response = await Api.get(`/dashboard/all-reviews?${params.toString()}`);

      if (response.success) {
        this.setState({
          reviews: response.reviews,
          pagination: response.pagination,
          loading: false
        });
      } else {
        this.setState({ error: 'Failed to fetch reviews', loading: false });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      this.setState({
        error: 'Failed to load reviews',
        loading: false
      });
    }
  };

  fetchStats = async () => {
    try {
      const response = await Api.get('/dashboard/review-stats');
      if (response.success) {
        this.setState({ stats: response.stats });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  handlePageChange = (newPage) => {
    this.setState(
      prevState => ({
        pagination: { ...prevState.pagination, page: newPage }
      }),
      this.fetchReviews
    );
  };

  handleFilterChange = (field, value) => {
    this.setState(
      prevState => ({
        filters: { ...prevState.filters, [field]: value },
        pagination: { ...prevState.pagination, page: 1 }
      }),
      this.fetchReviews
    );
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.fetchReviews();
  };

  renderStars(rating) {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= rating ? '#FFD700' : '#ddd', fontSize: '18px' }}>
          ★
        </span>
      );
    }
    return stars;
  }

  renderStats() {
    const { stats } = this.state;

    if (!stats) return null;

    return (
      <Pane display="flex" gap={16} marginBottom={24} flexWrap="wrap">
        <Pane
          background="tint2"
          padding={16}
          borderRadius={8}
          minWidth={150}
          textAlign="center"
        >
          <Heading size={400} color="muted">Total Reviews</Heading>
          <Text size={600} fontWeight="bold">{stats.totalReviews}</Text>
        </Pane>
        <Pane
          background="tint2"
          padding={16}
          borderRadius={8}
          minWidth={150}
          textAlign="center"
        >
          <Heading size={400} color="muted">Average Rating</Heading>
          <Text size={600} fontWeight="bold">
            {stats.averageRating} ★
          </Text>
        </Pane>
        {stats.ratingDistribution?.map(item => (
          <Pane
            key={item.rating}
            background="tint1"
            padding={12}
            borderRadius={8}
            minWidth={80}
            textAlign="center"
          >
            <Text size={500}>{item.rating}★</Text>
            <Text display="block" size={400} color="muted">
              {item.count} reviews
            </Text>
          </Pane>
        ))}
      </Pane>
    );
  }

  renderFilters() {
    const { filters } = this.state;

    return (
      <Pane display="flex" gap={16} marginBottom={24} alignItems="flex-end" flexWrap="wrap">
        <form onSubmit={this.handleSearchSubmit} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <TextInputField
            label="Search"
            placeholder="Order #, customer name, email..."
            value={filters.search}
            onChange={e => this.setState(prev => ({
              filters: { ...prev.filters, search: e.target.value }
            }))}
            marginBottom={0}
            width={250}
          />
          <Button type="submit" marginBottom={0}>
            Search
          </Button>
        </form>

        <SelectField
          label="Filter by Rating"
          value={filters.rating}
          onChange={e => this.handleFilterChange('rating', e.target.value)}
          marginBottom={0}
          width={150}
        >
          <option value="">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </SelectField>

        <SelectField
          label="Sort By"
          value={`${filters.sort}-${filters.direction}`}
          onChange={e => {
            const [sort, direction] = e.target.value.split('-');
            this.setState(
              prev => ({
                filters: { ...prev.filters, sort, direction },
                pagination: { ...prev.pagination, page: 1 }
              }),
              this.fetchReviews
            );
          }}
          marginBottom={0}
          width={180}
        >
          <option value="createdAt-DESC">Newest First</option>
          <option value="createdAt-ASC">Oldest First</option>
          <option value="rating-DESC">Highest Rating</option>
          <option value="rating-ASC">Lowest Rating</option>
        </SelectField>
      </Pane>
    );
  }

  renderPagination() {
    const { pagination } = this.state;

    if (pagination.totalPages <= 1) return null;

    return (
      <Pane display="flex" alignItems="center" justifyContent="center" gap={16} marginTop={24}>
        <IconButton
          icon={ChevronLeftIcon}
          disabled={pagination.page === 1}
          onClick={() => this.handlePageChange(pagination.page - 1)}
        />
        <Text>
          Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total)
        </Text>
        <IconButton
          icon={ChevronRightIcon}
          disabled={pagination.page === pagination.totalPages}
          onClick={() => this.handlePageChange(pagination.page + 1)}
        />
      </Pane>
    );
  }

  render() {
    const { reviews, loading, error } = this.state;

    return (
      <Pane padding={24}>
        <Heading size={700} marginBottom={24}>
          All Order Reviews
        </Heading>

        {this.renderStats()}
        {this.renderFilters()}

        {loading ? (
          <Pane display="flex" alignItems="center" justifyContent="center" padding={40}>
            <Spinner />
          </Pane>
        ) : error ? (
          <Alert intent="danger" title="Error loading reviews">
            {error}
          </Alert>
        ) : !reviews || reviews.length === 0 ? (
          <Alert intent="none" title="No reviews found">
            No order reviews match your criteria.
          </Alert>
        ) : (
          <>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>Order #</Table.TextHeaderCell>
                <Table.TextHeaderCell>Customer</Table.TextHeaderCell>
                <Table.TextHeaderCell>Rating</Table.TextHeaderCell>
                <Table.TextHeaderCell flexGrow={2}>Comment</Table.TextHeaderCell>
                <Table.TextHeaderCell>Order Value</Table.TextHeaderCell>
                <Table.TextHeaderCell>Date</Table.TextHeaderCell>
                <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {reviews.map((review) => (
                  <Table.Row key={review.id}>
                    <Table.TextCell>
                      <Link href={`/orders/${review.orderId}`} target="_blank" rel="noopener noreferrer">
                        #{review.orderNumber}
                      </Link>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Pane>
                        <Text size={400} display="block">
                          {review.customerName || 'N/A'}
                        </Text>
                        <Text size={300} color="muted">
                          {review.customerEmail || ''}
                        </Text>
                      </Pane>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Pane display="flex" alignItems="center">
                        {this.renderStars(review.rating)}
                        <Badge marginLeft={8} color={review.rating >= 4 ? 'green' : review.rating >= 3 ? 'yellow' : 'red'}>
                          {review.rating}/5
                        </Badge>
                      </Pane>
                    </Table.TextCell>
                    <Table.TextCell flexGrow={2}>
                      {review.comment ? (
                        <Text
                          size={400}
                          style={{
                            maxWidth: '400px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }}
                          title={review.comment}
                        >
                          {review.comment}
                        </Text>
                      ) : (
                        <Text size={400} color="muted">
                          No comment
                        </Text>
                      )}
                    </Table.TextCell>
                    <Table.TextCell>
                      <Text size={400}>
                        {review.orderGrandTotal ? `${review.orderGrandTotal} RON` : 'N/A'}
                      </Text>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Text size={400}>
                        {moment(review.createdAt).format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Link href={`/orders/${review.orderId}`} target="_blank" rel="noopener noreferrer">
                        View Order
                      </Link>
                    </Table.TextCell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {this.renderPagination()}
          </>
        )}
      </Pane>
    );
  }
}

function mapStateToProps(state) {
  return state;
}

export default connect(mapStateToProps)(AllReviews);
